
import {defineComponent} from 'vue';
import Slider from "@/components/rechner/Slider.vue";
import SliderTO from "@/types/SliderTO";
import {abrundenAufGanzzahl, aufrundenAufGanzzahl} from "@/utils/NumberUtil";
import Sondertilgung from "@/components/rechner/Sondertilgung.vue";
import Sollzinsbindung from "@/components/rechner/Sollzinsbindung.vue";
import {CalcTrigger, NoCalcTrigger} from "@/types/CalcTrigger";
import HinweisTO from "@/types/HinweisTO";
import HinweisMixin from "@/mixins/HinweisMixin";

export default defineComponent({
    name: 'Rechner',
    components: {Sollzinsbindung, Sondertilgung, Slider},
    mixins: [HinweisMixin],
    data() {
        return {
            isVolltilgung: false,
            widthFakeSlider: 50,
            isDarlehenAnBeleihungAngepasst: false,
            //Wird nur 1x aus dem store gelesen, die Manipulation erfolgt direkt hier, also nicht über den store,
            //da die Antwort sonst zu lange brauch, was zu unerwünschtem Verhalten des Sliders führt
            sliderDarlehen: this.$store.state.sliderDarlehen as SliderTO,

            textSliderKaufpreis: "",
            textToolTipKaufpreis: "",
            textSliderDarlehen: "",
            textToolTipDarlehen: "",
        };
    },
    methods: {
        //Schreiben der Werte in den Store
        onKaufpreisChanged(value: number): void {
            this.hideErrors();
            this.updateSliderDarlehen(value);
            this.$store.commit(CalcTrigger.KAUFPREIS, value);
        },
        onDarlehenChanged(value: number): void {
            this.hideErrors();
            this.sliderDarlehen.value = value;
            this.$store.commit(CalcTrigger.DARLEHEN, value);
        },
        onSlideKaufpreis(value: number): void {
            let oldDarlehen = this.sliderDarlehen.value;
            this.updateSliderDarlehen(value);
            this.isDarlehenAnBeleihungAngepasst = oldDarlehen > this.sliderDarlehen.max;
        },
        updateSliderDarlehen(kaufpreisValue: number): void {
            let kaufpreis = kaufpreisValue || this.$store.state.sliderPreis.value;

            //Min Preis muss um einen Schritt erhöht werden, bspw. von 50k auf 60k damit der Darlehenslider unterschiedliche Werte für min und max haben kann,
            //da das max vom Darlehenslider abhängig vom Wert des Kaufpreissliders ist
            let minPreis = aufrundenAufGanzzahl(this.sliderDarlehen.min / this.maxBeleihung * 100, this.sliderPreis.step) + this.sliderPreis.step;
            this.sliderPreis.min = minPreis;

            let maxDarlehen = abrundenAufGanzzahl(kaufpreis * this.maxBeleihung / 100, this.sliderDarlehen.step);
            this.sliderDarlehen.max = maxDarlehen;

            if (this.sliderDarlehen.value > maxDarlehen || this.isDarlehenAnBeleihungAngepasst) {
                this.sliderDarlehen.value = maxDarlehen;
                this.$store.commit(NoCalcTrigger.DARLEHEN, maxDarlehen);
                this.sliderDarlehen.hinweis = this.getHinweisDarlehenAnBeleihungAngepasst(this.maxBeleihung);
                this.isDarlehenAnBeleihungAngepasst = false;
            }
            this.updateFakeSlider(kaufpreis)
        },
        updateFakeSlider(kaufpreis: number): void {
            let sliderKaufpreis = this.$store.state.sliderPreis as SliderTO,
                diffMaxMin = sliderKaufpreis.max - sliderKaufpreis.min,
                anzahlSteps = diffMaxMin / sliderKaufpreis.step + 1,
                diffValueMin = kaufpreis - sliderKaufpreis.min,
                stepsToValue = diffValueMin / sliderKaufpreis.step + 1,
                prozent = stepsToValue / anzahlSteps;

            this.widthFakeSlider = 100 - prozent * this.maxBeleihung;

            if (this.widthFakeSlider < 0) {
                this.widthFakeSlider = 0;
            }
        },
        onSollzinsbindungChanged() {
            this.hideErrors();
            this.updateSliderDarlehen(this.sliderPreis.value);
        },
        onVolltilgungChanged(event: any) {
            this.hideErrors();
            this.isVolltilgung = event;
        },
        onKaufpreisOutOfRange(oldVal: number, newVal: number, min: number, max: number): void {
            this.sliderPreis.hinweis = this.getHinweisKaufpreisInRangeGesetzt(oldVal, newVal, min, max);
        },
        onDarlehenOutOfRange(oldVal: number, newVal: number, min: number, max: number): void {
            this.sliderDarlehen.hinweis = this.getHinweisDarlehenInRangeGesetzt(oldVal, newVal, min, max);
        },
        hideErrors(): void {
            this.sliderPreis.hinweis = {} as HinweisTO;
            this.sliderDarlehen.hinweis = {} as HinweisTO;
        }
    },
    computed:
        {
            //Lesen der Werte aus dem Store
            sliderPreis(): SliderTO {
                return this.$store.state.sliderPreis;
            },
            maxBeleihung(): number {
                let sollzinsbindungen = this.$store.state.sollzinsbindungen;
                let aktiveSollzinsbindung = this.$store.state.selectedSollzinsbindung;
                let maxBeleihung = 100;

                for (let sollzinsbindung of sollzinsbindungen) {
                    if (sollzinsbindung.dauer == aktiveSollzinsbindung && sollzinsbindung.maxBeleihungProzent < 100) {
                        maxBeleihung = sollzinsbindung.maxBeleihungProzent;
                    }
                }
                return maxBeleihung;
            },
            isInitDataLoaded(): boolean {
                return this.$store.state.initDataLoaded;
            },
        },
    watch: {
        isInitDataLoaded(isLoaded: boolean): void {
            if (isLoaded) {
                this.sliderDarlehen = this.$store.state.sliderDarlehen;
                this.updateSliderDarlehen(this.sliderPreis.value);

                this.textSliderKaufpreis = this.$store.state.fachtexte["rechner.berechnung.slider.kaufpreis"];
                this.textToolTipKaufpreis = this.$store.state.fachtexte["rechner.berechnung.tooltip.slider.kaufpreis"];
                this.textSliderDarlehen = this.$store.state.fachtexte["rechner.berechnung.slider.darlehen"];
                this.textToolTipDarlehen = this.$store.state.fachtexte["rechner.berechnung.tooltip.slider.darlehen"];
            }
        },
    }
});
